<template>
  <div class="email-attachments-compose">
    <!-- Header -->
    <div class="attachments-header">
      <div class="header-left">
        <div class="header-icon">
          <v-icon size="18" color="#372EAA">mdi-paperclip</v-icon>
        </div>
        <span class="header-title">
          {{ attachments.length > 0 ? attachments.length : $t('components.email.attachments.no_attachments') }}
          {{ $t('components.email.attachments.attachments') }}
        </span>
      </div>
      <div class="header-right" v-if="attachments.length > 0">
        <span class="total-size">{{ cumulateAttachmentSize() }} {{ $t('components.email.attachments.size_unit') }}</span>
      </div>
    </div>

    <!-- Attachments List -->
    <div class="attachments-list" v-if="attachments.length > 0">
      <div
        class="attachment-item"
        v-for="(attach, key) in firstArray"
        :key="key"
      >
        <div class="attachment-info">
          <div class="file-icon">
            <img :src="getFileIcon(attach.name)" :alt="attach.name" />
          </div>
          <div class="file-details">
            <span class="file-name">{{ attach.name }}</span>
            <span class="file-size">{{ (attach.size * 0.000001).toFixed(2) }} {{ $t('components.email.attachments.size_unit_mb') }}</span>
          </div>
        </div>
        <button
          type="button"
          class="remove-btn"
          @click="removeFileFromList(key)"
          :title="$t('components.email.attachments.remove') || 'Remove'"
        >
          <v-icon size="16">mdi-close</v-icon>
        </button>
      </div>

      <!-- More attachments indicator -->
      <div
        class="more-attachments-btn"
        v-if="firstArray.length < attachments.length"
        @click="openAttachmentDialog()"
      >
        <v-icon size="16" color="#372EAA">mdi-plus</v-icon>
        <span>{{ attachments.length - firstArray.length }} {{ $t('components.email.attachments.more') || 'more' }}</span>
      </div>
    </div>

    <!-- Empty State -->
    <div class="attachments-empty" v-else>
      <v-icon size="24" color="#c7cae8">mdi-paperclip</v-icon>
      <span>{{ $t('components.email.attachments.no_files') || 'No files attached' }}</span>
    </div>
  </div>
</template>

<script>
import { returnFileTypeImage } from "../../../../helpers/file";
import EmailExpandedFileReader from "../../file-management/EmailExpandedFileReader.vue";
export default {
  components: { EmailExpandedFileReader },
  props: {
    attachments: {
      type: Array,
      default: [],
    },
  },
  computed:
  {
    firstArray(){
      if (this.attachments.length > 3) {
        return this.attachments.slice(0, 2);
      } else {
        return this.attachments;
      }
    }
  },
  methods: {
    getFileIcon(filename) {
      return returnFileTypeImage(filename);
    },
    /**
     * Method that opens email create/edit modal window.
     */
    openAttachmentDialog() {
      // Opens the dialog for a new email draft
      this.$emit("openEditFiles")
    },

    cumulateAttachmentSize() {
      let cumulated_size = (
        this.attachments.map((p) => p.size).reduce((a, b) => a + b, 0) *
        0.000001
      ).toFixed(2);
      return cumulated_size;
    },
    removeFileFromList(index) {
      console.log("delete idx", index);

      this.$emit("deleteAttach", index);
    },
    
    downloadAttachment(attachment) {
      // Decode Base64 content
      const byteCharacters = atob(attachment.contentBytes);

      // Convert to byte array
      const byteNumbers = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      // Create Blob
      const blob = new Blob([byteNumbers], { type: attachment.contentType });

      // Create a download link
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = attachment.name || this.$t('components.email.attachments.downloaded_file');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

  },
};
</script>

<style scoped>
.email-attachments-compose {
  width: 100%;
}

/* Header */
.attachments-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #F0F1FD 0%, #E8E9F8 100%);
  border-radius: 8px;
}

.header-title {
  font-family: "Barlow", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #2b2b41;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.header-right {
  display: flex;
  align-items: center;
}

.total-size {
  font-family: "Barlow", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #606076;
  background-color: #f0f1f4;
  padding: 4px 10px;
  border-radius: 12px;
}

/* Attachments List */
.attachments-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.attachment-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border: 1.5px solid #e3e5ee;
  border-radius: 10px;
  padding: 10px 12px;
  min-width: 200px;
  max-width: 280px;
  flex: 1 1 200px;
  transition: all 0.2s ease;
}

.attachment-item:hover {
  border-color: #c7cae8;
  box-shadow: 0 2px 8px rgba(55, 46, 170, 0.08);
}

.attachment-info {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  flex: 1;
}

.file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #F8F9FC 0%, #F0F1FD 100%);
  border-radius: 8px;
  flex-shrink: 0;
}

.file-icon img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.file-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}

.file-name {
  font-family: "Barlow", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #2b2b41;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.file-size {
  font-family: "Barlow", sans-serif;
  font-size: 11px;
  font-weight: 400;
  color: #9ca3af;
}

.remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: 8px;
}

.remove-btn:hover {
  background-color: #fef2f2;
}

.remove-btn .v-icon {
  color: #9ca3af !important;
  transition: color 0.2s ease;
}

.remove-btn:hover .v-icon {
  color: #ef4444 !important;
}

/* More attachments button */
.more-attachments-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: linear-gradient(135deg, #F0F1FD 0%, #E8E9F8 100%);
  border: 1.5px dashed #c7cae8;
  border-radius: 10px;
  padding: 10px 16px;
  min-width: 100px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.more-attachments-btn:hover {
  background: linear-gradient(135deg, #E8E9F8 0%, #DBD9F7 100%);
  border-color: #372EAA;
}

.more-attachments-btn span {
  font-family: "Barlow", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #372EAA;
}

/* Empty State */
.attachments-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
  background: #fafbfc;
  border: 1.5px dashed #e3e5ee;
  border-radius: 10px;
}

.attachments-empty span {
  font-family: "Barlow", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #9ca3af;
}
</style>
